import React from "react";
import { ShieldCheck, Lock, Headphones, Award } from "lucide-react";

/**
 * Site-wide "Payment Processed By" banner.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │            PAYMENT PROCESSED BY                                   │
 *   │       ┌─────────────┐        ┌─────────────┐                      │
 *   │       │  Razorpay   │        │  PayGlocal  │                      │
 *   │       └─────────────┘        └─────────────┘                      │
 *   │ ─────────────────────────────────────────────────────────────    │
 *   │            Accepted payment methods                               │
 *   │  [VISA][MC][AMEX][DISC][RuPay][UPI][Wallets][Netbanking][Intl]   │
 *   │ ─────────────────────────────────────────────────────────────    │
 *   │  🛡 100% Secure   🔒 SSL Encrypted   🎧 24/7 Support   🏆 Trusted  │
 *   └──────────────────────────────────────────────────────────────────┘
 */
export default function PaymentProcessBanner() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-5 py-6 sm:py-8" data-testid="payment-process-banner">
      <div className="rounded-2xl bg-white shadow-[0_18px_50px_-24px_rgba(15,23,42,0.35)] border border-slate-200 overflow-hidden">

        {/* Row 1 — Payment gateways */}
        <div className="px-4 sm:px-8 pt-6 sm:pt-8 pb-6">
          <div className="flex justify-center mb-5">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-bold tracking-[0.25em] uppercase text-white bg-blue-600 px-4 py-1.5 rounded-full shadow-[0_6px_18px_-6px_rgba(37,99,235,0.6)]"
              data-testid="ppb-heading"
            >
              Payment Processed By
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10" data-testid="ppb-gateways">
            <GatewayLogo src="/brand-razorpay.png" alt="Razorpay" testid="ppb-razorpay-logo" href="https://razorpay.com"/>
            <div className="hidden sm:block h-12 w-px bg-gradient-to-b from-transparent via-slate-300 to-transparent" aria-hidden/>
            <GatewayLogo src="/brand-payglocal.png" alt="PayGlocal" testid="ppb-payglocal-logo" href="https://payglocal.in"/>
          </div>
        </div>

        {/* Row 2 — Accepted payment methods */}
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 sm:px-8 py-5 sm:py-6">
          <div className="text-center text-[11px] sm:text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">
            Accepted Payment Methods
          </div>
          <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-2.5" data-testid="ppb-methods">
            <VisaMark/>
            <MastercardMark/>
            <AmexMark/>
            <DiscoverMark/>
            <RuPayMark/>
            <UpiMark/>
            <WalletChip/>
            <NetbankingChip/>
            <InternationalChip/>
          </div>
        </div>

        {/* Row 3 — Trust bar */}
        <div className="bg-blue-700" data-testid="ppb-trust-bar">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5">
            <ul className="grid grid-cols-2 lg:grid-cols-4 gap-y-2.5 gap-x-3 text-white">
              <TrustChip icon={<ShieldCheck size={16}/>} title="100% Secure" subtitle="Transactions"/>
              <TrustChip icon={<Lock size={16}/>}         title="SSL Encrypted" subtitle="Payments"/>
              <TrustChip icon={<Headphones size={16}/>}   title="24/7 Customer" subtitle="Support"/>
              <TrustChip icon={<Award size={16}/>}        title="Trusted &" subtitle="Reliable"/>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  BUILDING BLOCKS  ───────────────────────── */

function GatewayLogo({ src, alt, testid, href }) {
  const img = (
    <img
      src={src}
      alt={alt}
      className="h-10 sm:h-12 lg:h-14 w-auto object-contain"
      data-testid={testid}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
    />
  );
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
      {img}
    </a>
  ) : img;
}

function TrustChip({ icon, title, subtitle }) {
  return (
    <li className="flex items-center gap-2">
      <div className="shrink-0 h-7 w-7 rounded-full bg-white/10 flex items-center justify-center border border-white/25">
        {icon}
      </div>
      <div className="leading-tight">
        <div className="font-semibold text-[12px]">{title}</div>
        <div className="text-blue-100 text-[10px]">{subtitle}</div>
      </div>
    </li>
  );
}

/* ─────────────────────────  METHOD CHIPS  ───────────────────────── */

const chipCls =
  "inline-flex items-center justify-center h-9 sm:h-10 min-w-[62px] px-3 bg-white border border-slate-200 rounded-md shadow-[0_1px_3px_rgba(15,23,42,0.06)] hover:shadow-[0_4px_10px_-4px_rgba(15,23,42,0.15)] hover:-translate-y-[1px] transition-all";

function VisaMark() {
  return (
    <span className={chipCls} title="Visa" data-testid="ppb-method-visa">
      <svg viewBox="0 0 60 22" width="46" height="18">
        <text x="0" y="17" fontFamily="Arial Black, Arial, sans-serif" fontSize="17" fontWeight="900" fontStyle="italic" fill="#1A1F71">VISA</text>
      </svg>
    </span>
  );
}
function MastercardMark() {
  return (
    <span className={chipCls} title="Mastercard" data-testid="ppb-method-mastercard">
      <svg viewBox="0 0 40 24" width="34" height="20">
        <circle cx="14" cy="12" r="9" fill="#EB001B"/>
        <circle cx="26" cy="12" r="9" fill="#F79E1B"/>
        <path d="M20 5.4a9 9 0 0 0 0 13.2 9 9 0 0 0 0-13.2z" fill="#FF5F00"/>
      </svg>
    </span>
  );
}
function AmexMark() {
  return (
    <span className={chipCls} title="American Express" data-testid="ppb-method-amex">
      <svg viewBox="0 0 60 26" width="48" height="20">
        <rect width="60" height="26" fill="#006FCF" rx="3"/>
        <text x="6" y="14" fontFamily="Arial Black, Arial, sans-serif" fontSize="7" fontWeight="900" fill="#ffffff">AMERICAN</text>
        <text x="6" y="22" fontFamily="Arial Black, Arial, sans-serif" fontSize="7" fontWeight="900" fill="#ffffff">EXPRESS</text>
      </svg>
    </span>
  );
}
function DiscoverMark() {
  return (
    <span className={chipCls} title="Discover" data-testid="ppb-method-discover">
      <svg viewBox="0 0 90 22" width="64" height="18">
        <text x="0" y="16" fontFamily="Arial Black, Arial, sans-serif" fontSize="13" fontWeight="900" fill="#231F20">DISC</text>
        <circle cx="52" cy="11" r="6" fill="#FF6000"/>
        <text x="60" y="16" fontFamily="Arial Black, Arial, sans-serif" fontSize="13" fontWeight="900" fill="#231F20">VER</text>
      </svg>
    </span>
  );
}
function RuPayMark() {
  return (
    <span className={chipCls} title="RuPay" data-testid="ppb-method-rupay">
      <svg viewBox="0 0 80 22" width="58" height="18">
        <text x="0" y="16" fontFamily="Arial Black, Arial, sans-serif" fontSize="15" fontWeight="900" fill="#0F66E9" fontStyle="italic">Ru</text>
        <text x="22" y="16" fontFamily="Arial Black, Arial, sans-serif" fontSize="15" fontWeight="900" fill="#EF4444" fontStyle="italic">Pay</text>
        <polygon points="60,4 72,11 60,18" fill="#22C55E"/>
      </svg>
    </span>
  );
}
function UpiMark() {
  return (
    <span className={chipCls} title="UPI" data-testid="ppb-method-upi">
      <svg viewBox="0 0 60 22" width="52" height="20">
        <text x="0"  y="16" fontFamily="Arial Black, Arial, sans-serif" fontSize="13" fontWeight="900" fill="#097939">U</text>
        <text x="12" y="16" fontFamily="Arial Black, Arial, sans-serif" fontSize="13" fontWeight="900" fill="#ED752E">P</text>
        <text x="24" y="16" fontFamily="Arial Black, Arial, sans-serif" fontSize="13" fontWeight="900" fill="#00457C">I</text>
        <polygon points="34,6 44,11 34,16" fill="#0f766e"/>
      </svg>
    </span>
  );
}
function WalletChip() {
  return (
    <span className={chipCls} title="Wallet (Paytm · PhonePe · Amazon Pay · MobiKwik)" data-testid="ppb-method-wallet">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#1D6DFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
        <path d="M20 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2z"/>
        <path d="M2 10h20M16 14h.01"/>
      </svg>
      <span className="text-[11px] sm:text-xs font-bold text-slate-800">Wallets</span>
    </span>
  );
}
function NetbankingChip() {
  return (
    <span className={chipCls} title="Netbanking (SBI · HDFC · ICICI · Axis and more)" data-testid="ppb-method-netbanking">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#0F66E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
        <path d="M3 10 12 3l9 7v11H3zM8 21V10M12 21V10M16 21V10"/>
      </svg>
      <span className="text-[11px] sm:text-xs font-bold text-slate-800">Netbanking</span>
    </span>
  );
}
function InternationalChip() {
  return (
    <span className={chipCls} title="All International Cards Accepted" data-testid="ppb-method-international">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#0891B2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
        <circle cx="12" cy="12" r="10"/>
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
      <span className="text-[11px] sm:text-xs font-bold text-slate-800">International Cards</span>
    </span>
  );
}
